import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import useStateHandler from '@/hooks/useStateHandler';
import GalleryService from '@/services/gallery.service';
import UserService from '@/services/user.service';

import HeaderArtist from './HeaderArtist';
import HeaderCollector from './HeaderCollector';
import HeaderGallery from './HeaderGallery';
import OnBoardingButton from './OnBoardingButton';

import './styles/OnBoardingThemeSelector.css';

/**
 * Guard: recuerda el modo actual (light/dark) y lo restaura si algún efecto
 * lo pisa tras cambiar el theme. Hace dos comprobaciones muy cortas.
 */
function withModeGuard(doWork) {
  const wasLight = document.documentElement.getAttribute('data-mode') === 'light';

  const restore = () => {
    const nowLight = document.documentElement.getAttribute('data-mode') === 'light';
    if (nowLight !== wasLight) {
      if (wasLight) {
        document.documentElement.setAttribute('data-mode', 'light');
      } else {
        // Dark = default en :root → sin atributo
        document.documentElement.removeAttribute('data-mode');
      }
    }
  };

  // Ejecuta la acción (set de theme + state updates)
  doWork();

  // Reafirma en el siguiente frame y después del microtask queue
  requestAnimationFrame(() => {
    restore();
    setTimeout(restore, 0);
  });
}

export default function OnBoardingThemeSelector({ user_type }) {
  const { state, stateHandler, cacheHandler } = useStateHandler();
  const navigate = useNavigate();

  const sanitizeUsername = (value = "") => value.replace(/\s+/g, "").toLowerCase();
  const getCurrentMode = () => (document.documentElement.getAttribute('data-mode') === 'light' ? 'light' : 'dark');
  const persistUserThemeAndMode = async () => {
    if (!selectedTheme) return;
    const mode = getCurrentMode();
    stateHandler.set("user_session.user_preferences.theme", selectedTheme);
    stateHandler.set("user_session.user_preferences.mode", mode);
    await UserService.updateUserPreferences({
      "user_preferences.theme": selectedTheme,
      "user_preferences.mode": mode,
    });
  };

  const [selectedTheme, setSelectedTheme] = useState("");
  const [username, setUsername] = useState(sanitizeUsername(state.user_session.user_username || ""));
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  // Cambia theme y actualiza estado
  const changeTheme = (theme) => {
    withModeGuard(() => {
      stateHandler.set("user_session.user_preferences.theme", theme);
      setSelectedTheme(theme);
    });
  };

  const isActive = (theme) => {
    return selectedTheme && state.user_session.user_preferences.theme === theme;
  };

  const isDiveIntoSuarteDisabled = !selectedTheme || !username || loading;

  const onDiveIntoSuarteClick = async (e) => {
    e.preventDefault();
    if (isDiveIntoSuarteDisabled) return;
    setLoading(true);
    setError(null);

    const accountData = {
      user_username: username,
      user_preferences: { 
        theme: selectedTheme,
        mode: getCurrentMode(),
      },
    };

    const { response, data } = await UserService.createAccountAndCompleteOnboarding(accountData, user_type);

    if (response.ok) {
      stateHandler.set("user_session", data);
      await persistUserThemeAndMode();
      cacheHandler.storeInCache("canUserCompleteOnboarding", true, { type: "@cache/no-expiration" });
    } else if (response.status === 400) {
      setError({ element: data.error_data.element, error_code: data.error_type });
    } else if ([401, 403].includes(response.status)) {
      navigate("/", { replace: true });
    }
    setLoading(false);
  };

  const onGalleryClick = async (e) => {
    e.preventDefault();
    if (isDiveIntoSuarteDisabled) return;
    setLoading(true);
    const { response, data } = await GalleryService.completeOnboarding();
    if (response.ok) {
      stateHandler.set("user_session", data);
      await persistUserThemeAndMode();
      cacheHandler.storeInCache("canUserCompleteOnboarding", true, { type: "@cache/no-expiration" });
    } else if (response.status === 400) {
      setError({ element: data.error_data.element, error_code: data.error_type });
    } else if ([401, 403].includes(response.status)) {
      navigate("/", { replace: true });
    }
    setLoading(false);
  };

  const renderHeader = () => {
    switch (user_type) {
    case 'gallery':
      return <HeaderGallery selectedTheme={selectedTheme} state={state} />;
    case 'collector':
      return <HeaderCollector selectedTheme={selectedTheme} error={error} state={state} username={username} setUsername={setUsername} />;
    case 'artist':
      return <HeaderArtist selectedTheme={selectedTheme} error={error} state={state} username={username} setUsername={setUsername} />;
    default:
      return <HeaderCollector selectedTheme={selectedTheme} error={error} state={state} username={username} setUsername={setUsername} />;
    }
  };

  const onSubmitClick = (e) => {
    switch (user_type) {
    case 'gallery':
      return onGalleryClick(e);
    case 'collector':
      return onDiveIntoSuarteClick(e);
    default:
      return onDiveIntoSuarteClick(e);
    }
  };

  return (
    <>
      {renderHeader()}

      {/* WRAPPER para controlar ancho/espaciado en desktop */}
      <div className="onboarding-shell">
        <div className="onboarding_selecting-theme-container">
          <OnBoardingButton aria-label="Starry moon theme" className="theme-circle starry" isActive={isActive("starry_moon")} onClick={() => changeTheme("starry_moon")} />
          <OnBoardingButton aria-label="Dancing on the grass theme" className="theme-circle dancing" isActive={isActive("dancing-on-the-grass")} onClick={() => changeTheme("dancing-on-the-grass")} />
          <OnBoardingButton aria-label="Fountain of delights theme" className="theme-circle fountain" isActive={isActive("fountain-of-delights")} onClick={() => changeTheme("fountain-of-delights")} />
          <OnBoardingButton aria-label="Swinging dress theme" className="theme-circle water" isActive={isActive("swinging-dress")} onClick={() => changeTheme("swinging-dress")} />
          <OnBoardingButton aria-label="Water lily pond theme" className="theme-circle violet" isActive={isActive("water-lily-pond")} onClick={() => changeTheme("water-lily-pond")} />
        </div>

        <div className="onboarding_dive-button-container">
          <button
            type="button"
            className={`onboarding_dive-button ${isDiveIntoSuarteDisabled ? 'is-disabled' : 'is-enabled'}`}
            onClick={onSubmitClick}
            disabled={isDiveIntoSuarteDisabled}
            aria-disabled={isDiveIntoSuarteDisabled ? 'true' : 'false'}
            aria-busy={loading ? 'true' : 'false'}
          >
            {loading ? 'Loading…' : 'Dive into Suarte'}
          </button>
        </div>
      </div>
    </>
  );
}
